// Angular
import { Injectable, inject } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';

// Third party
import moment from 'moment';

// Local library
import { ICustomerBetSummaryModel, IGetAccountReportExRequest } from '@amwager/util-ts';

/**
 * A single bet-summary row with the derived percentage and
 * profit/loss figures the UI renders.
 */
export interface IBetSummaryRow {
  eventName: string;
  poolId: string;
  wagered: number;
  percent: number;    // share of total wagered
  winning: number;
  pl: number;         // winning - wagered
  plPercent: number;  // pl as % of wagered
}

/**
 * Pure form + calculation helpers for the Bet Summary view, so the
 * component keeps only template-facing handlers. Mirrors StatementsService.
 */
@Injectable({ providedIn: 'root' })
export class BetSummaryService {
  private readonly formBuilder = inject(FormBuilder);

  /**
   * Build the reactive form. Bet summary is filtered by date range only.
   * @returns {FormGroup} form with dateFrom, dateTo
   */
  buildForm(): FormGroup {
    return this.formBuilder.group({
      dateFrom: [moment().subtract(1, 'days').toDate(), [Validators.required]],
      dateTo: [new Date(), [Validators.required]]
    });
  }

  /**
   * Map the form state to a bet summary API request
   * @param {FormGroup} form The bet summary form
   * @returns {IGetAccountReportExRequest} Request payload
   */
  buildRequest(form: FormGroup): IGetAccountReportExRequest {
    const value = form.getRawValue();
    return {
      startDate: value.dateFrom,
      endDate: value.dateTo
    };
  }

  /**
   * True when "Date From" is after "Date To"
   * @param {FormGroup} form The bet summary form
   * @returns {boolean} Whether the range is invalid
   */
  isDateRangeInvalid(form: FormGroup): boolean {
    const from = form.get('dateFrom')?.value;
    const to = form.get('dateTo')?.value;
    if (!from || !to) return false;
    return moment(from).isAfter(moment(to));
  }

  /**
   * Map raw records to display rows with derived percent + P/L
   * @param {ICustomerBetSummaryModel[]} records Raw API rows
   * @returns {IBetSummaryRow[]} Rows enriched with percent and P/L
   */
  buildRows(records: ICustomerBetSummaryModel[]): IBetSummaryRow[] {
    const totalWagered = this.sumWagered(records);
    return records.map((record) => {
      const wagered = record.Wagered ?? 0;
      const winning = record.Winning ?? 0;
      const pl = winning - wagered;
      return {
        eventName: record.EventName ?? '',
        poolId: record.PoolId ?? '',
        wagered,
        percent: totalWagered ? (wagered / totalWagered) * 100 : 0,
        winning,
        pl,
        plPercent: wagered ? (pl / wagered) * 100 : 0
      };
    });
  }

  private sumWagered(records: ICustomerBetSummaryModel[]): number {
    return records.reduce((sum, record) => sum + (record.Wagered ?? 0), 0);
  }
}
